import React, { type ReactNode, useEffect, useState } from 'react';
import { ArrowDownward, ArrowUpward, Clear, FilterList, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface FilterConfig {
  key: string;
  label: string;
  type: 'select' | 'text';
  options?: { value: string; label: string }[];
}

interface Column<T = Record<string, unknown>> {
  key: keyof T & string;
  label: string;
  render?: (value: T[keyof T], row: T) => ReactNode;
  sortable?: boolean;
}

interface FilterableDataTableProps<T = Record<string, unknown>> {
  title: string;
  columns: Column<T>[];
  data: T[];
  total?: number;
  loading?: boolean;
  page?: number;
  perPage?: number;
  filters?: FilterConfig[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  onFiltersChange?: (filters: Record<string, string>) => void;
  onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  onRowClick?: (row: T, event?: React.MouseEvent) => void;
  getRowUrl?: (row: T) => string;
  emptyMessage?: string;
  className?: string;
  /**
   * Show the free-text search box.
   *
   * Defaults to true, which is how this component has always behaved — the
   * bespoke admin pages that use it forward `filters.search` to endpoints
   * that honor it. The generic resource list does not, so it opts out rather
   * than rendering a box that silently does nothing (GDN-2734).
   */
  searchable?: boolean;
}

export function FilterableDataTable<T = Record<string, unknown>>({
  title,
  columns,
  data,
  total,
  loading = false,
  page = 1,
  perPage = 20,
  filters = [],
  sortBy,
  sortOrder,
  onPageChange,
  onPerPageChange,
  onFiltersChange,
  onSortChange,
  onRowClick,
  getRowUrl,
  emptyMessage = 'No data available',
  className,
  searchable = true,
}: FilterableDataTableProps<T>) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [showFilters, setShowFilters] = useState(false);

  // Combined effect for all filter changes with debounced search
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        const newFilters = { ...activeFilters };
        if (searchTerm.trim()) {
          newFilters.search = searchTerm.trim();
        } else {
          delete newFilters.search;
        }
        onFiltersChange?.(newFilters);
      },
      searchTerm ? 300 : 0,
    ); // Debounce search, immediate for other filters

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm, activeFilters, onFiltersChange]);

  const handleFilterChange = (filterKey: string, value: string) => {
    const newFilters = { ...activeFilters };
    if (value) {
      newFilters[filterKey] = value;
    } else {
      // Use destructuring to omit the key instead of delete
      const { [filterKey]: _, ...rest } = newFilters;
      setActiveFilters(rest);
      return;
    }
    setActiveFilters(newFilters);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setActiveFilters({});
    onFiltersChange?.({});
  };

  const handleSort = (columnKey: string) => {
    if (!onSortChange) return;

    let newSortOrder: 'asc' | 'desc' = 'asc';

    // If clicking the same column, toggle the sort order
    if (sortBy === columnKey) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }

    onSortChange(columnKey, newSortOrder);
  };

  const getSortIcon = (columnKey: string) => {
    if (sortBy !== columnKey) return null;
    return sortOrder === 'asc' ? (
      <ArrowUpward className="ml-1 h-4 w-4" />
    ) : (
      <ArrowDownward className="ml-1 h-4 w-4" />
    );
  };

  const activeFilterCount = Object.keys(activeFilters).length + (searchTerm ? 1 : 0);

  return (
    <div className={className}>
      {/* Header with search and filter controls */}
      <div className="bg-white rounded-lg shadow mb-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            {loading && <CircularProgress size={18} />}
          </div>

          {/* Search and Filter Row */}
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex-1 max-w-md">
              {searchable && (
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={`Search ${title.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                  }}
                 slotProps={{ input: {
                    startAdornment: <Search className="mr-2 h-4 w-4 text-gray-500" />,
                  } }}/>
              )}
            </div>

            <div className="flex items-center gap-2">
              {activeFilterCount > 0 && (
                <Chip
                  label={`${String(activeFilterCount)} filter${activeFilterCount > 1 ? 's' : ''} active`}
                  size="small"
                  onDelete={clearAllFilters}
                  color="primary"
                  variant="outlined"
                />
              )}

              {filters.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setShowFilters(!showFilters);
                  }}
                  startIcon={<FilterList />}
                >
                  Filters
                </Button>
              )}

              {activeFilterCount > 0 && (
                <Button variant="text" size="small" onClick={clearAllFilters} startIcon={<Clear />}>
                  Clear All
                </Button>
              )}
            </div>
          </div>

          {/* Filter Controls */}
          {showFilters && filters.length > 0 && (
            <Box className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filters.map(filter => (
                  <FormControl key={filter.key} size="small" fullWidth>
                    <InputLabel>{filter.label}</InputLabel>
                    {filter.type === 'select' ? (
                      <Select
                        value={activeFilters[filter.key] || ''}
                        label={filter.label}
                        onChange={e => {
                          handleFilterChange(filter.key, e.target.value);
                        }}
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {filter.options?.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <TextField
                        value={activeFilters[filter.key] || ''}
                        label={filter.label}
                        onChange={e => {
                          handleFilterChange(filter.key, e.target.value);
                        }}
                        size="small"
                      />
                    )}
                  </FormControl>
                ))}
              </div>
            </Box>
          )}
        </div>
      </div>

      {/* Sortable Data Table */}
      <div className="bg-white rounded-lg shadow mb-4">
        {data.length === 0 ? (
          <div className="px-6 py-12 flex items-center justify-center">
            {loading ? (
              <CircularProgress size={28} />
            ) : (
              <p className="text-gray-500">{emptyMessage}</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map(column => (
                    <th
                      key={column.key}
                      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        column.sortable !== false ? 'cursor-pointer hover:bg-gray-100' : ''
                      }`}
                      onClick={() => {
                        if (column.sortable !== false) handleSort(column.key);
                      }}
                    >
                      <div className="flex items-center">
                        {column.label}
                        {column.sortable !== false && getSortIcon(column.key)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((row, index) => {
                  const rowUrl = getRowUrl?.(row);

                  if (rowUrl) {
                    return (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={event => {
                          if (event.metaKey || event.ctrlKey) {
                            window.open(rowUrl, '_blank');
                            return;
                          }
                          navigate(rowUrl);
                        }}
                        onAuxClick={event => {
                          if (event.button === 1) {
                            window.open(rowUrl, '_blank');
                          }
                        }}
                      >
                        {columns.map(column => (
                          <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                            {column.render
                              ? column.render(row[column.key as keyof T], row)
                              : String(row[column.key as keyof T] ?? '')}
                          </td>
                        ))}
                      </tr>
                    );
                  }

                  return (
                    <tr
                      key={index}
                      className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                      onClick={event => onRowClick?.(row, event)}
                    >
                      {columns.map(column => (
                        <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                          {column.render
                            ? column.render(row[column.key as keyof T], row)
                            : String(row[column.key as keyof T] ?? '')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total && total > perPage && (
        <div className="bg-white rounded-lg shadow px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {Math.min((page - 1) * perPage + 1, total)} to{' '}
              {Math.min(page * perPage, total)} of {total} results
            </div>

            <div className="flex items-center gap-4">
              <FormControl size="small">
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={perPage}
                  label="Per Page"
                  onChange={e => onPerPageChange?.(Number(e.target.value))}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>

              <div className="flex items-center gap-2">
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page <= 1}
                  onClick={() => onPageChange?.(page - 1)}
                >
                  Previous
                </Button>

                <span className="text-sm text-gray-700">
                  Page {page} of {Math.ceil(total / perPage)}
                </span>

                <Button
                  variant="outlined"
                  size="small"
                  disabled={page >= Math.ceil(total / perPage)}
                  onClick={() => onPageChange?.(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
